import Clarity from '@microsoft/clarity';
import { Backdrop, CircularProgress, CssBaseline } from '@mui/material';
import { useSnackbar } from 'notistack';
import React, { useEffect } from 'react';
import { Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { setCloseSnackBarRef, setSnackBarRef } from '../../components/admin/common/NotiService';
import AgentAlertsManager from '../../components/AgentAlerts/AgentAlertsManager';
import ChatWidget from '../../components/BroadCast/ChatWidget';
import OZSidenav from '../../components/OZSidenav/OZSidenav';
import OZToolbar from '../../components/OZToolbar/OZToolbar';
import SubUserChatWidget from '../../components/SubUserChat/components/chat/SubUserChatWidget';
import { OZContext } from '../../contexts/OZContext';
import { SettingsProvider } from '../../contexts/SettingsContext';
import { getUserId, isSuperAdminLoggedIn } from '../../services/auth-service';
import { getLocationFromProperties } from '../../services/page-service';
import { deleteValueInStorage } from '../../services/storage-service';
import { LOCAL_STORAGE } from '../../utils/constants';
import { msClarityProjectID } from '../../utils/properties';
import { checkRoles, checkSettings, generateFourLetterRandomString } from '../../utils/util';
import Admin from '../admin/Admin';
import OAuthIntegration from '../admin/Integration/OAuthIntegration';
import Apps from '../Apps/Apps';
import CreateDashboard from '../CreateDashboard/CreateDashboard';
import Dashboard from '../Dashboard/Dashboard';
import DataManagement from '../DataManagement/DataManagement';
import ErrorComponent from '../Error/Error';
import ErrorBoundary from '../Error/ErrorBoundary';
import LandingPage from '../Home/LandingPage';
import CommandCenter from '../persona/CommandCenter';
import Playground from '../Playground/Playground';
import Presets from '../Presets/Presets';
import Wallboard from '../Wallboard/Wallboard';
import '../WireframeDashboard/WireframeDashboard.css';

const ReportsWireframe = React.lazy(() => import('../reports/ReportsWireframe'));

function Wireframe() {
	const navigate = useNavigate();
	const location = useLocation();
	const { selectedDashboard, setSelectedDashboard, isLoaderOpen } = React.useContext(OZContext);
	const [wallboardEdit, setWallboardEdit] = React.useState<any>(false)
	const [enableSubUserChat, setEnableSubUserChat] = React.useState<boolean>(false);
	const usLocation = getLocationFromProperties() === 'us';
	const hasSlashcallRole = checkRoles('ROLE_SLASHCALL');
	const getInitialDisableAllPages = () => {
		if (!hasSlashcallRole) {
			return false;
		}
		if (isSuperAdminLoggedIn()) {
			return false;
		}
		return true;
	};
	const [disableAllPages, setDisableAllPages] = React.useState<boolean>(getInitialDisableAllPages());

	useEffect(() => {
		if (hasSlashcallRole && !isSuperAdminLoggedIn()) {
			checkSettings("SHOW_ONLY_PERSONA").then((resp) => {
				if (resp && (resp === true || resp === "true")) {
					setDisableAllPages(true);
				} else {
					setDisableAllPages(false);
				}
			}).catch((error) => {
				console.error("Error checking SHOW_ONLY_PERSONA setting", error);
			});
		}

		if (msClarityProjectID) {
			checkSettings("CAPTURE_MS_CLRTY").then((resp) => {
				if (resp && (resp === true || resp === "true")) {
					Clarity.init(msClarityProjectID);
					Clarity.identify(getUserId())
					// Clarity.event('LoggedIn')
				}
			})
		}

		const checkSubUserChat = async () => {
			try {
				const resp = await checkSettings("AGENT_SUB_USER_CHAT");
				if (resp && (resp === true || resp === "true")) {
					setEnableSubUserChat(true);
				} else {
					setEnableSubUserChat(false);
				}
				localStorage.setItem('ivr_access_key', generateFourLetterRandomString());
			} catch (error) {
				console.error("Error checking sub user chat", error);
			}
		}
		checkSubUserChat();
	}, [])

	const handleDashboardSelect = (event: any) => {
		setSelectedDashboard(event);
		if (event?.dashboard?.dashboardName === 'Wallboard') {
			setWallboardEdit(false);
			navigate('/wallboard');
		} else {
			navigate('/dashboard');
		}
	};
	const handleDashboardEdit = (event: any) => {
		setSelectedDashboard(event);
		if (event?.dashboard?.dashboardName === 'Wallboard') {
			setWallboardEdit(true);
			navigate('/wallboard');
		} else {
			navigate('/create');
		}
	};
	React.useEffect(() => {
		if (location.pathname === '/preset') {
			deleteValueInStorage(LOCAL_STORAGE.SELECTED_DASHBOARD);
			setSelectedDashboard({});
		}
	}, [location]);
	const { enqueueSnackbar, closeSnackbar } = useSnackbar();
	React.useEffect(() => {
		setSnackBarRef(enqueueSnackbar);
		setCloseSnackBarRef(closeSnackbar);
	}, [enqueueSnackbar, closeSnackbar])



	return (
		<div>
			<SettingsProvider>
				<Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={isLoaderOpen}>
					<CircularProgress color="inherit" />
				</Backdrop>
				<CssBaseline />
				<OZToolbar onSelectDashboard={handleDashboardSelect}></OZToolbar>
				{disableAllPages ?
					<React.Suspense
						fallback={
							<Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={true}>
								<CircularProgress color="inherit" />
							</Backdrop>
						}
					>
						<Routes>
							<Route path={`/command-center`} element={<CommandCenter disableAllPages={true} />} />
						</Routes>
					</React.Suspense>
					:
					<>
						{usLocation && <AgentAlertsManager />}
						{enableSubUserChat ? <SubUserChatWidget /> : <ChatWidget />}
						<OZSidenav></OZSidenav>
						<ErrorBoundary fallback={<ErrorComponent errorType='ErrorBoundary' />}>
							<main className="content">
								<div className="toolbar" />
								<React.Suspense
									fallback={
										<Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={true}>
											<CircularProgress color="inherit" />
										</Backdrop>
									}
								>
									<Routes>
										<Route path={`/home`} element={<LandingPage />} />
										<Route path={`/dashboard`} element={<Dashboard selectedDashboard={selectedDashboard} />} />
										<Route path={`/preset`} element={<Presets onPresetClick={handleDashboardSelect} onPresetEdit={handleDashboardEdit} />} />
										<Route path={`/create`} element={<CreateDashboard selectedDashboard={selectedDashboard} />} />
										<Route path={`/playground`} element={<Playground />} />
										<Route path={`/wallboard`} element={<Wallboard wallboardOnEdit={wallboardEdit} />} />
										<Route path={`/reports/*`} element={<ReportsWireframe />}></Route>
										<Route path={`/admin/*`} element={<Admin />} />
										<Route path={`/apps/*`} element={<Apps />} />
										<Route path={`/oauth`} element={<OAuthIntegration />} />
										<Route path={`/command-center`} element={<CommandCenter disableAllPages={false} />} />
										{checkRoles('ROLE_RULE_ENGINE') && <Route path={`/data-management`} element={<DataManagement />} />}
										<Route path="*" element={<ErrorComponent />}></Route>
									</Routes>
								</React.Suspense>
							</main>
						</ErrorBoundary>
					</>
				}
			</SettingsProvider>
		</div>
	);
}

export default Wireframe;
